import { WhereFilterOp } from '@firebase/firestore-types'
import {
  DocumentData,
  DocumentSnapshot,
  endBefore,
  getDoc,
  getDocs,
  limit,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  QuerySnapshot,
  startAfter,
  where
} from 'firebase/firestore'

import AFirestore from './AFirestore'
import { Filter, IDatabaseRead, OrderBy, Query } from './IDatabaseRead'

class ReadFirestore extends AFirestore implements IDatabaseRead {
  /**
   * Get documents array from one collection
   * @param query
   * @returns
   */
  get(query: Query): Promise<QuerySnapshot<DocumentData, DocumentData>> {
    return getDocs(this.getAllQuery(query))
  }
  /**
   * Get one document from collection
   * @returns
   */
  getOne(): Promise<DocumentSnapshot> {
    return getDoc(this.getDocument())
  }
  /**
   * Get one document from collection and keep the listener active for changes
   * @param onSuccess - method to deal with values returned
   * @param onError - method to deal with error
   * @returns - listener function
   */
  getOneStream(
    onSuccess: (doc: DocumentSnapshot) => void,
    onError?: (error: Error) => void
  ): () => void {
    return onSnapshot(this.getDocument(), onSuccess, onError)
  }
  /**
   * Get documents from collection and keep the listener active for changes
   * @param query - Query object to filter, order and limit the search
   * @param onSuccess - method to deal with values returned
   * @param onError - method to deal with error
   * @returns - listener function
   */
  getStream(
    query: Query,
    onSuccess: (snapshot: QuerySnapshot<DocumentData, DocumentData>) => void,
    onError?: (error: Error) => void
  ): () => void {
    return onSnapshot(this.getAllQuery(query), onSuccess, onError)
  }
  private getAllQuery(queryFilter: Query) {
    const collection = this.getCollection()
    if (!queryFilter) {
      return query(collection)
    }
    const queryBuilder = []

    const wheres = this.addFilters(queryFilter.filters)
    queryBuilder.push(...wheres)

    const sorts = this.addSort(queryFilter.orderBy)
    queryBuilder.push(...sorts)

    const startAfter = this.addStartAfter(queryFilter.startAfter)
    if (startAfter) queryBuilder.push(startAfter)

    const endBefore = this.addEndBefore(queryFilter.endBefore)
    if (endBefore) queryBuilder.push(endBefore)

    let limit = null
    if (queryFilter.endBefore) {
      limit = this.addLimitToLast(queryFilter.limit)
    } else {
      limit = this.addLimit(queryFilter.limit)
    }
    if (limit) queryBuilder.push(limit)

    return query(collection, ...queryBuilder)
  }
  private addFilters(filters: Array<Filter>) {
    const wheres = []
    if (filters) {
      for (const filter of filters) {
        wheres.push(
          where(filter.field, filter.condition as WhereFilterOp, filter.value)
        )
      }
    }
    return wheres
  }
  private addSort(orderBySort: Array<OrderBy>) {
    const sorts = []
    if (orderBySort) {
      for (const order of orderBySort) {
        sorts.push(orderBy(order.field, order.sort))
      }
    }
    return sorts
  }
  private addLimit(limitResults: number) {
    return limitResults ? limit(limitResults) : null
  }
  private addLimitToLast(limit: number) {
    return limit ? limitToLast(limit) : null
  }
  private addStartAfter(lastVisible: DocumentSnapshot<DocumentData>) {
    return lastVisible ? startAfter(lastVisible) : null
  }
  private addEndBefore(fistVisible: DocumentSnapshot<DocumentData>) {
    return fistVisible ? endBefore(fistVisible) : null
  }
}

export default ReadFirestore
